<template>
  <div class="panorama-page p-3 p-md-4 dark-theme">
    <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap">
      <div class="d-flex align-items-center mb-2 mb-md-0">
        <h3 class="text-white mb-0 font-weight-bold tracking-wide mr-3">
          <b-icon icon="display" class="mr-2 text-warning"></b-icon>策略全景解析
        </h3>
        <div class="d-flex align-items-center bg-dark-pill px-2 py-1 rounded-pill border border-secondary-40">
          <b-icon icon="calendar3" class="text-warning mx-2" font-scale="0.9"></b-icon>
          <b-form-input 
            type="date" 
            v-model="selectedDate" 
            size="sm" 
            class="custom-dark-datepicker border-0 bg-transparent text-white shadow-none px-1" 
            style="width: 135px; font-size: 0.875rem;"
            @change="fetchData"
          ></b-form-input>
          <b-button 
            variant="link" 
            size="sm" 
            class="text-warning p-0 mx-2 text-decoration-none xx-small" 
            @click="resetToToday"
            v-if="selectedDate !== todayStr"
          >
            今天
          </b-button>
        </div>
        <div class="d-flex align-items-center bg-dark-pill px-3 py-1 rounded-pill border border-secondary-40 ml-2">
          <b-form-checkbox
            v-model="supplementH67"
            @change="fetchData"
            switch
            class="text-white custom-dark-switch xx-small"
          >
            <span style="font-size: 0.825rem; font-weight: 500; cursor: pointer;">补充H6/7</span>
          </b-form-checkbox>
        </div>
      </div>
      <b-button variant="outline-light" class="rounded-pill px-4 shadow-sm" @click="$router.back()">
        <b-icon icon="arrow-left" class="mr-1"></b-icon> 返回
      </b-button>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="warning" label="Loading..."></b-spinner>
      <div class="text-muted-gold mt-3">正在获取 {{ selectedDate }} 监控数据...</div>
    </div>

    <div v-else-if="topics.length === 0" class="text-center py-5">
      <b-icon icon="inbox" font-scale="3" class="text-muted mb-3"></b-icon>
      <div class="text-muted">{{ selectedDate }} 无监控题材或暂未获取到数据</div>
    </div>

    <div v-else class="row no-gutters custom-row-5">
      <div v-for="(topic, index) in topics" :key="index" class="col-12 col-md-4 col-xl-20 mb-2 px-1">
        <b-card class="bg-card-dark border-light-10 shadow-sm h-100 rounded-lg overflow-hidden" body-class="p-1 p-md-2">
          <!-- 头部 -->
          <div class="d-flex justify-content-between align-items-start mb-2 pb-2 border-bottom border-light-10">
            <div>
              <h6 class="mb-1 text-white font-weight-bold cursor-pointer hover-primary d-flex align-items-center flex-wrap" :title="topic.topic_name" @click="showTopicKline(topic)">
                <span>{{ topic.topic_name }}</span>
                <span v-if="topic.h_marker || topic.d20_marker" class="text-muted-gold font-weight-normal xx-small ml-1" style="font-size: 0.75rem;">
                  [{{ [topic.h_marker, topic.d20_marker].filter(Boolean).join(',') }}]
                </span>
              </h6>
              <b-badge variant="soft-warning" class="xx-small mb-1 d-inline-block">共 {{ topic.total_candidates }} 备选</b-badge>
              <div class="text-muted xx-small" v-if="topic.stats">
                涨跌: <span class="text-danger">{{ topic.stats.up_count }}</span>/<span class="text-success">{{ topic.stats.down_count }}</span>
                <span class="mx-1">|</span>
                均: <span :class="getPnlClass(topic.stats.avg_pct)">{{ topic.stats.avg_pct.toFixed(2) }}%</span>
              </div>
              <div class="text-muted xx-small mt-1" style="max-height: 2.8em; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.4;" v-if="topic.stats" :title="topic.stats.main_leader + ' (主) / ' + topic.stats.gem_leader + ' (创)'">
                领涨: <span class="text-white">{{ topic.stats.main_leader }} / {{ topic.stats.gem_leader }}</span>
              </div>
              <div class="text-muted xx-small mt-1" style="max-height: 2.8em; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.4;" :title="topic.stats.limit_ups" v-if="topic.stats">
                涨停: <span class="text-danger">{{ topic.stats.limit_ups }}</span>
              </div>
            </div>
            <div class="text-right">
              <div class="text-muted xx-small">触发/限额</div>
              <div class="text-white xx-small mt-1">
                <span class="text-danger font-weight-bold">{{ topic.alerts.length }}</span>次
                <span class="mx-1">|</span>
                主<span :class="{'text-warning': topic.counts['主板'] >= 2}">{{ topic.counts['主板'] || 0 }}/2</span>
                <span class="mx-1">|</span>
                创<span :class="{'text-warning': topic.counts['双创'] >= 1}">{{ topic.counts['双创'] || 0 }}/1</span>
              </div>
            </div>
          </div>
          
          <!-- 备选股列表 -->
          <div class="table-responsive">
            <table class="table table-dark table-hover table-borderless table-sm mb-0 compact-table xx-small">
              <thead>
                <tr class="text-muted border-bottom border-light-10">
                  <th style="width: 28%">股票</th>
                  <th class="text-right">现价</th>
                  <th class="text-right">额/市值(亿)</th>
                  <th class="text-right">涨跌幅</th>
                  <th class="text-center text-nowrap" style="width: 22%">状态</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stock in getDisplayCandidates(topic.candidates, topic.topic_name)" :key="stock.code6">
                  <td class="align-middle">
                    <a href="javascript:void(0)" @click="showStockInfo(stock.code6, stock.name)" class="text-white text-decoration-none hover-primary text-truncate d-inline-block" style="max-width: 60px;">
                      {{ stock.name }}
                    </a>
                    <span class="text-muted ml-1" style="transform: scale(0.85); display: inline-block;">{{ stock.code6 }}</span>
                  </td>
                  <td class="text-right align-middle font-weight-bold" :class="getPnlClass(stock.pct_change)">
                    {{ stock.price.toFixed(2) }}
                  </td>
                  <td class="text-right align-middle text-muted text-nowrap">
                    {{ (stock.amount / 100000000).toFixed(1) }}/{{ (stock.mkv / 100000000).toFixed(1) }}
                  </td>
                  <td class="text-right align-middle font-weight-bold text-nowrap" :class="getPnlClass(stock.pct_change)">
                    {{ stock.pct_change > 0 ? '+' : '' }}{{ stock.pct_change.toFixed(2) }}%
                  </td>
                  <td class="text-center align-middle text-nowrap">
                    <div class="d-flex align-items-center justify-content-center">
                      <div>
                        <b-badge v-if="stock.triggered" variant="danger" style="font-size: 0.65rem; padding: 0.15em 0.3em;">
                          触发{{ stock.trigger_time ? `(${stock.trigger_time})` : '' }}
                        </b-badge>
                        <b-badge v-else-if="stock.filter_reason && (stock.filter_reason.indexOf('避') === 0 || stock.filter_reason.indexOf('避雷') > -1)" variant="danger" style="font-size: 0.65rem; padding: 0.15em 0.3em;">
                          {{ stock.filter_reason }}
                        </b-badge>
                        <b-badge v-else-if="stock.filter_reason === '占位'" variant="warning" style="font-size: 0.65rem; padding: 0.15em 0.3em;">
                          占位{{ stock.trigger_time ? `(${stock.trigger_time})` : '' }}
                        </b-badge>
                        <span v-else-if="stock.filter_reason" class="text-muted" style="font-size: 0.65rem;">
                          {{ stock.filter_reason }}
                        </span>
                        <b-badge v-else-if="stock.is_met_buy_condition" class="badge-met-condition text-nowrap" style="font-size: 0.65rem; padding: 0.15em 0.3em;">
                          未触发(已达)
                        </b-badge>
                        <b-badge v-else variant="secondary" style="font-size: 0.65rem; padding: 0.15em 0.3em;">
                          未触发
                        </b-badge>
                      </div>
                      
                      <!-- 手动触发按钮 (仅今天且未触发时显示) -->
                      <b-button 
                        v-if="selectedDate === todayStr && !stock.triggered"
                        variant="link" 
                        size="sm" 
                        class="text-info p-0 ml-2 xx-small manual-trigger-btn font-weight-bold" 
                        title="手动点火买入"
                        @click.stop="confirmManualTrigger(stock.code6, stock.name)"
                      >
                        点火
                      </b-button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="topic.candidates.length > 10" class="text-center mt-1">
              <b-button variant="link" size="sm" class="text-muted xx-small p-0" @click="toggleExpand(topic.topic_name)">
                {{ expandedTopics[topic.topic_name] ? '收起' : `查看更多 (${topic.candidates.length - 10})` }}
                <b-icon :icon="expandedTopics[topic.topic_name] ? 'chevron-up' : 'chevron-down'"></b-icon>
              </b-button>
            </div>
          </div>
        </b-card>
      </div>
    </div>
    
    <!-- 引入个股侧边栏 -->
    <StockInfoSidebar 
      :symbol_code="sidebarSymbolCode" 
      :symbol_name="sidebarSymbolName" 
    />

    <!-- 题材K线图模态框 -->
    <TopicKlineModal
      :visible="showTopicKlineModal"
      :uniqueKey="selectedTopicUniqueKey"
      :topicName="selectedTopicName"
      @close="showTopicKlineModal = false"
    />
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'
import TopicKlineModal from '@/components/plate/TopicKlineModal.vue'

export default {
  name: 'WonderStrategyPanorama',
  components: {
    StockInfoSidebar,
    TopicKlineModal
  },
  data() {
    const d = new Date()
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const todayStr = `${year}-${month}-${day}`
    return {
      loading: true,
      topics: [],
      expandedTopics: {},
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      showTopicKlineModal: false,
      selectedTopicUniqueKey: '',
      selectedTopicName: '',
      selectedDate: todayStr,
      supplementH67: false
    }
  },
  computed: {
    todayStr() {
      const d = new Date()
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    toggleExpand(topicName) {
      this.$set(this.expandedTopics, topicName, !this.expandedTopics[topicName])
    },
    getDisplayCandidates(candidates, topicName) {
      if (!candidates) return []
      const isExpanded = this.expandedTopics[topicName]
      if (isExpanded || candidates.length <= 10) {
        return candidates
      }
      return candidates.slice(0, 10)
    },
    showStockInfo(code, name) {
      this.sidebarSymbolCode = code || ''
      this.sidebarSymbolName = name || ''
      this.$nextTick(() => {
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
      })
    },
    resetToToday() {
      this.selectedDate = this.todayStr
      this.fetchData()
    },
    async fetchData() {
      this.loading = true
      try {
        const params = {
          supplement_h67: this.supplementH67
        }
        if (this.selectedDate) {
          params.date = this.selectedDate
        }
        const res = await request_zzquant({
          url: '/v3/topic/monitor/panorama',
          method: 'get',
          params
        })
        if (res.data && (res.data.code === 200 || res.data.code === 20000)) {
          this.topics = res.data.data
        } else {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '获取数据失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('网络请求异常', {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },
    getPnlClass(val) {
      if (val > 0) return 'text-danger'
      if (val < 0) return 'text-success'
      return 'text-muted'
    },
    showTopicKline(topic) {
      if (topic && topic.unique_key) {
        this.selectedTopicUniqueKey = topic.unique_key
        this.selectedTopicName = topic.topic_name
        this.showTopicKlineModal = true
      }
    },
    confirmManualTrigger(code6, name) {
      this.$bvModal.msgBoxConfirm(`确定要为 [${name} (${code6})] 手动点火执行买入吗？`, {
        title: '手动点火确认',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: '确认点火',
        cancelTitle: '取消',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
      .then(value => {
        if (value) {
          this.triggerManual(code6)
        }
      })
      .catch(err => {
        console.error(err)
      })
    },
    async triggerManual(code6) {
      try {
        const res = await request_zzquant({
          url: '/v3/topic/monitor/trigger_manual',
          method: 'post',
          data: {
            code: code6
          }
        })
        if (res.data && res.data.code === 200) {
          this.$bvToast.toast(`个股 ${code6} 手动点火成功！`, {
            title: '成功',
            variant: 'success',
            solid: true
          })
          this.fetchData()
        } else {
          this.$bvToast.toast(res.data?.msg || res.data?.message || '点火失败', {
            title: '错误',
            variant: 'danger',
            solid: true
          })
        }
      } catch (e) {
        console.error(e)
        const errMsg = e.response?.data?.detail || '网络请求异常'
        this.$bvToast.toast(errMsg, {
          title: '错误',
          variant: 'danger',
          solid: true
        })
      }
    }
  }
}
</script>

<style scoped>
.panorama-page {
  min-height: 100vh;
  background-color: #0f1219;
}
.bg-card-dark {
  background-color: #1a1e29;
}
.border-light-10 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
.text-muted-gold {
  color: #bfa77b !important;
}
.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.15) !important;
  color: #ffc107 !important;
}
.table-dark {
  background-color: transparent;
}
.hover-primary:hover {
  color: var(--primary) !important;
}
.table-hover tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05);
}
.x-small {
  font-size: 0.75rem;
}
.xx-small {
  font-size: 0.65rem;
}
.compact-table td, .compact-table th {
  padding: 0.15rem 0.2rem !important;
}
.custom-row-5 > .col-xl-20 {
  flex: 0 0 20%;
  max-width: 20%;
}
.badge-met-condition {
  background-color: rgba(139, 92, 246, 0.2) !important;
  color: #c084fc !important;
  border: 1px solid rgba(139, 92, 246, 0.4) !important;
}
.bg-dark-pill {
  background-color: rgba(255, 255, 255, 0.08);
}
.border-secondary-40 {
  border-color: rgba(255, 255, 255, 0.15) !important;
}
.custom-dark-datepicker::-webkit-calendar-picker-indicator {
  filter: invert(0.8);
  cursor: pointer;
}
.manual-trigger-btn {
  color: #17a2b8 !important;
  text-decoration: none;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
}
.manual-trigger-btn:hover {
  color: #138496 !important;
  transform: scale(1.15);
  text-shadow: 0 0 5px rgba(23, 162, 184, 0.5);
}
</style>